import { useState, useRef, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useAuth } from '../contexts/AuthContext'
import { useLocalizedNavigate } from '../hooks/useLocalizedNavigate'

export default function UserMenu() {
  const { t } = useTranslation()
  const navigate = useLocalizedNavigate()
  const { user, credits, logout, loading } = useAuth()
  const [open, setOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([])

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Keyboard navigation within dropdown
  const handleMenuKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setOpen(false)
      triggerRef.current?.focus()
      return
    }
    const items = itemRefs.current.filter(Boolean) as HTMLButtonElement[]
    const currentIndex = items.indexOf(document.activeElement as HTMLButtonElement)
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      const next = currentIndex < items.length - 1 ? currentIndex + 1 : 0
      items[next]?.focus()
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      const prev = currentIndex > 0 ? currentIndex - 1 : items.length - 1
      items[prev]?.focus()
    }
  }, [])

  const handleToggle = () => {
    const willOpen = !open
    setOpen(willOpen)
    if (willOpen) {
      requestAnimationFrame(() => itemRefs.current[0]?.focus())
    }
  }

  if (loading) return null

  if (!user) {
    return (
      <button className="btn btn-outline" style={{ height: '2.5rem', whiteSpace: 'nowrap' }} onClick={() => navigate('/login')}>
        <FontAwesomeIcon icon="lock" />
        {t('auth.signIn')}
      </button>
    )
  }

  const menuItems = [
    { icon: 'plus' as const, label: t('home.createNew'), action: () => navigate('/create') },
    { icon: 'user' as const, label: t('auth.myAccount'), action: () => navigate('/account') },
    { icon: 'credit-card' as const, label: t('auth.pricing'), action: () => navigate('/pricing') },
  ]

  return (
    <div className="user-menu" ref={menuRef}>
      <button
        ref={triggerRef}
        className="user-menu-trigger"
        onClick={handleToggle}
        aria-expanded={open}
        aria-haspopup="true"
      >
        <div className="user-avatar" aria-hidden="true">
          {user.displayName.charAt(0).toUpperCase()}
        </div>
        <span className="user-name">{user.displayName}</span>
        {credits > 0 && (
          <span className="credit-badge">{credits}</span>
        )}
      </button>

      {open && (
        <div className="user-menu-dropdown" role="menu" onKeyDown={handleMenuKeyDown}>
          <div className="user-menu-header">
            <strong>{user.displayName}</strong>
            <span className="text-sm text-gray">{user.email}</span>
          </div>
          <div className="user-menu-divider" role="separator" />
          {menuItems.map((item, i) => (
            <button
              key={item.label}
              ref={(el) => { itemRefs.current[i] = el }}
              className="user-menu-item"
              role="menuitem"
              onClick={() => { setOpen(false); item.action() }}
            >
              <FontAwesomeIcon icon={item.icon} />
              {item.label}
            </button>
          ))}
          <div className="user-menu-divider" role="separator" />
          <button
            ref={(el) => { itemRefs.current[menuItems.length] = el }}
            className="user-menu-item"
            role="menuitem"
            onClick={async () => { setOpen(false); await logout(); navigate('/') }}
          >
            <FontAwesomeIcon icon="arrow-right-from-bracket" />
            {t('auth.signOut')}
          </button>
        </div>
      )}
    </div>
  )
}
